import {jsPDF} from 'jspdf';
import 'jspdf-autotable';
import {Invoice,Client,ClientJob,BusinessSettings,InvoiceDefaults} from '../types';
import {formatCurrency,formatDate} from './formatters';
import payIdBrandmarkUrl from '../assets/payid-brandmark.svg';

const safeText=(value: any): string=> String(value || '');
const DEFAULT_PDF_ACCENT='#0f766e';

const resolvePayIdBrandmarkPngDataUrl=(): Promise<string | null>=> new Promise(resolve=> {
  const image=new Image();
  image.onload=()=> {
    try {
      const sourceWidth=image.naturalWidth || 300;
      const sourceHeight=image.naturalHeight || 212;
      // The official SVG includes generous artboard margins. Crop only that empty
      // margin so the original brandmark paths remain unchanged and legible in print.
      const sourceX=sourceWidth * 0.12;
      const sourceY=sourceHeight * 0.33;
      const sourceCropWidth=sourceWidth * 0.76;
      const sourceCropHeight=sourceHeight * 0.34;
      const canvas=document.createElement('canvas');
      canvas.width=1200;
      canvas.height=Math.round(canvas.width * sourceCropHeight / sourceCropWidth);
      const context=canvas.getContext('2d');
      if (!context) {
        resolve(null);
        return;
      }
      context.drawImage(image,sourceX,sourceY,sourceCropWidth,sourceCropHeight,0,0,canvas.width,canvas.height);
      resolve(canvas.toDataURL('image/png'));
    } catch (error) {
      console.warn('Official PayID brandmark could not be prepared for the invoice PDF.',error);
      resolve(null);
    }
  };
  image.onerror=()=> resolve(null);
  image.src=payIdBrandmarkUrl;
});

const hexToRgb=(hex: string): [number,number,number]=> {
  const normalized=safeText(hex).trim();
  return /^#[0-9a-f]{6}$/i.test(normalized) ? [parseInt(normalized.slice(1,3),16),parseInt(normalized.slice(3,5),16),parseInt(normalized.slice(5,7),16)] : [15,118,110];
};

const shouldShowPaidStamp=(invoice: Invoice): boolean=> {
  const status=String(invoice.status || '').toLowerCase();
  const blockedStatuses=['draft','cancelled','canceled','void'];
  if (blockedStatuses.includes(status)) {
    return false;
  }
  const balanceDue=Number(invoice.balanceDue || 0);
  return balanceDue <=0.005;
};

const drawPaidStamp=(doc: any): void=> {
  const pageWidth=doc.internal.pageSize.getWidth();
  const pageHeight=doc.internal.pageSize.getHeight();
  const savedFont=doc.getFont();
  const savedFontSize=doc.getFontSize();
  const savedTextColor=doc.getTextColor();
  let opacityApplied=false;
  try {
    const gState=new (doc as any).GState({opacity: 0.18});
    doc.setGState(gState);
    opacityApplied=true;
  } catch {/* Opacity not supported */}
  if (opacityApplied) {
    doc.setTextColor(34,197,94);
  } else {
    doc.setTextColor(187,247,208);
  }
  doc.setFont('helvetica','bold');
  doc.setFontSize(90);
  doc.text('PAID',pageWidth / 2,pageHeight / 2,{align: 'center',angle: -35});
  if (opacityApplied) {
    try {
      const resetState=new (doc as any).GState({opacity: 1});
      doc.setGState(resetState);
    } catch {/* Ignore reset failure */}
  }
  doc.setFont(savedFont.fontName,savedFont.fontStyle);
  doc.setFontSize(savedFontSize);
  doc.setTextColor(savedTextColor.r || 0,savedTextColor.g || 0,savedTextColor.b || 0);
};

const getClientFacingInvoiceStatus=(invoice: Invoice): string=> {
  if (invoice.status==='cancelled') return 'CANCELLED';
  if (invoice.status==='void') return 'VOID';
  const balanceDue=Number(invoice.balanceDue || 0);
  if (balanceDue <=0) return 'PAID';
  const dueDate=new Date(invoice.dueDate);
  const today=new Date();
  dueDate.setHours(0,0,0,0);
  today.setHours(0,0,0,0);
  if (dueDate < today) return 'OVERDUE';
  if (Number(invoice.amountPaid || 0) > 0) {
    return 'PART PAID';
  }
  return 'AMOUNT DUE';
};

async function resolveLogoDataUrl(businessSettings: BusinessSettings): Promise<string | null> {
  const embeddedLogo=safeText(businessSettings.logoDataUrl).trim();
  if (/^data:image\/(png|jpe?g);base64,/i.test(embeddedLogo)) {
    return embeddedLogo;
  }
  const externalUrl=safeText(businessSettings.logoUrl).trim();
  if (!externalUrl) {
    return null;
  }
  try {
    const response=await fetch(externalUrl);
    if (!response.ok) {
      console.warn('Invoice logo URL could not be loaded.');
      return null;
    }
    const blob=await response.blob();
    const mimeType=blob.type.toLowerCase();
    if (!['image/png','image/jpeg','image/jpg'].includes(mimeType)) {
      console.warn('Invoice logo URL must resolve to PNG or JPEG.');
      return null;
    }
    return new Promise((resolve)=> {
      const reader=new FileReader();
      reader.onloadend=()=> resolve(reader.result as string);
      reader.onerror=(error)=> {
        console.warn('Failed to read logo blob.',error);
        resolve(null);
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Invoice logo URL could not be loaded.',error);
    return null;
  }
}

export const generateInvoicePdf=async (
  invoice: Invoice,
  client: Client,
  job: ClientJob | undefined,
  _payments: any[],
  businessSettings: BusinessSettings,
  invoiceDefaults?: InvoiceDefaults
)=> {
  const doc=new jsPDF() as any;
  const currency=invoice.currency || 'AUD';
  const accentRgb=hexToRgb(invoiceDefaults?.pdfAccentColor || DEFAULT_PDF_ACCENT);
  const logoDataUrl=await resolveLogoDataUrl(businessSettings);
  const configuredUploadedLogo=safeText(businessSettings.logoDataUrl).trim();
  if (configuredUploadedLogo && !logoDataUrl) {
    throw new Error('An uploaded invoice logo is configured but could not be read. Re-upload a standard PNG or JPEG logo in Settings and click Save All.');
  }

  let businessY=25;
  if (logoDataUrl) {
    try {
      const logoFormat=/^data:image\/jpe?g/i.test(logoDataUrl) ? 'JPEG' : 'PNG';
      const imageProperties=doc.getImageProperties(logoDataUrl);
      const maxLogoWidth=46;
      const maxLogoHeight=18;
      const originalWidth=Number(imageProperties.width) || maxLogoWidth;
      const originalHeight=Number(imageProperties.height) || maxLogoHeight;
      const ratio=Math.min(maxLogoWidth / originalWidth,maxLogoHeight / originalHeight);
      const logoWidth=originalWidth * ratio;
      const logoHeight=originalHeight * ratio;
      doc.addImage(logoDataUrl,logoFormat,15,12,logoWidth,logoHeight);
      businessY=Math.max(36,12 + logoHeight + 6);
    } catch (error) {
      console.error('Invoice logo embedding failed.',error);
      if (businessSettings.logoDataUrl) {
        throw new Error('The uploaded invoice logo could not be embedded. Re-upload a standard PNG or JPEG logo in Settings.');
      }
      console.warn('External invoice logo could not be embedded. PDF generated without logo.');
    }
  }

  // Header
  doc.setFontSize(24);
  doc.setFont('helvetica','bold');
  doc.setTextColor(...accentRgb);
  doc.text('INVOICE',195,25,{align: 'right'});
  doc.setFontSize(10);
  doc.setTextColor(0,0,0);
  doc.text(`Invoice #: ${safeText(invoice.invoiceNumber)}`,195,35,{align: 'right'});
  doc.text(`Date: ${formatDate(invoice.issueDate)}`,195,40,{align: 'right'});
  doc.text(`Due: ${formatDate(invoice.dueDate)}`,195,45,{align: 'right'});
  doc.text(`Status: ${getClientFacingInvoiceStatus(invoice)}`,195,50,{align: 'right'});

  // Business Details
  doc.setFontSize(12);
  doc.setFont('helvetica','bold');
  doc.text(safeText(businessSettings.businessName),15,businessY);
  doc.setFont('helvetica','normal');
  doc.setFontSize(9);
  doc.text(`ABN: ${safeText(businessSettings.abn)}`,15,businessY + 5);
  doc.text(safeText(businessSettings.addressLine1),15,businessY + 10);
  doc.text(`${safeText(businessSettings.suburb)},${safeText(businessSettings.state)} ${safeText(businessSettings.postcode)}`,15,businessY + 15);
  doc.text(safeText(businessSettings.email),15,businessY + 20);

  // Client Details
  doc.setFontSize(10);
  doc.setFont('helvetica','bold');
  doc.text('BILL TO:',15,75);
  doc.setFont('helvetica','normal');
  doc.text(safeText(client.name),15,80);
  if (client.companyName) doc.text(safeText(client.companyName),15,85);
  doc.text(safeText(client.addressLine1),15,90);
  doc.text(`${safeText(client.suburb)},${safeText(client.state)} ${safeText(client.postcode)}`,15,95);

  if (job) {
    doc.setFont('helvetica','bold');
    doc.text('PROJECT:',15,105);
    doc.setFont('helvetica','normal');
    doc.text(safeText(job.jobName),15,110);
  }

  // Items Table
  const tableRows=invoice.items.map(item=> {
    let desc=safeText(item.description);
    if (item.servicePeriodStart || item.servicePeriodEnd) {
      const start=item.servicePeriodStart ? formatDate(item.servicePeriodStart) : '...';
      const end=item.servicePeriodEnd ? formatDate(item.servicePeriodEnd) : '...';
      desc +=`\nService Period: ${start} - ${end}`;
    }
    let taxLabel='0%';
    if (item.taxTreatment==='gst_10') taxLabel=`${(item.taxRate * 100).toFixed(0)}%`;
    else if (item.taxTreatment==='custom') taxLabel=`${(item.taxRate * 100).toFixed(1)}%`;
    else if (item.taxTreatment==='gst_free') taxLabel='GST Free';
    else if (item.taxTreatment==='no_gst') taxLabel='No GST';
    else if (item.taxTreatment==='export') taxLabel='Export';
    return [
      desc,
      item.quantity.toString(),
      formatCurrency(item.unitPrice,currency),
      item.discount > 0 ? formatCurrency(item.discount,currency) : '-',
      taxLabel,
      formatCurrency(item.total,currency)
    ];
  });

  doc.autoTable({
    startY: job ? 120 : 110,
    head: [['Description','Qty','Unit Price','Disc','Tax','Total']],
    body: tableRows,
    headStyles: {fillColor: accentRgb},
    styles: {overflow: 'linebreak',valign: 'top'},
    columnStyles: {0: {overflow: 'linebreak'},5: {halign: 'right'}},
    rowPageBreak: 'avoid'
  });

  let finalY=(doc as any).lastAutoTable.finalY + 10;
  const invoicePageHeight=doc.internal.pageSize.getHeight();
  if (finalY + 45 > invoicePageHeight - 15) {
    doc.addPage();
    finalY=20;
  }

  // Totals
  doc.setFontSize(10);
  doc.text('Subtotal:',140,finalY);
  doc.text(formatCurrency(invoice.subtotal,currency),195,finalY,{align: 'right'});
  doc.text('Tax:',140,finalY + 5);
  doc.text(formatCurrency(invoice.taxAmount,currency),195,finalY + 5,{align: 'right'});
  doc.setFont('helvetica','bold');
  doc.text('Total:',140,finalY + 12);
  doc.text(formatCurrency(invoice.total,currency),195,finalY + 12,{align: 'right'});
  doc.setFont('helvetica','normal');
  doc.text('Amount Paid:',140,finalY + 20);
  doc.text(formatCurrency(invoice.amountPaid || 0,currency),195,finalY + 20,{align: 'right'});
  doc.setFont('helvetica','bold');
  doc.text('Balance Due:',140,finalY + 28);
  doc.setTextColor(...accentRgb);
  doc.text(formatCurrency(invoice.balanceDue,currency),195,finalY + 28,{align: 'right'});

  // Payment Details & Terms
  doc.setTextColor(0,0,0);
  doc.setFontSize(9);
  let currentY=finalY + 45;
  if (invoiceDefaults?.bankName) {
    doc.setFont('helvetica','bold');
    doc.text('Payment Details:',15,currentY);
    doc.setFont('helvetica','normal');
    doc.text(`Bank: ${safeText(invoiceDefaults.bankName)}`,15,currentY + 5);
    doc.text(`BSB: ${safeText(invoiceDefaults.bsb)}`,15,currentY + 10);
    doc.text(`Account: ${safeText(invoiceDefaults.accountNumber)}`,15,currentY + 15);
    currentY +=25;
  }
  doc.setTextColor(100,100,100);
  doc.setFontSize(8);
  const renderLongText=(title: string,text: string,yPos: number)=> {
    let y=yPos;
    doc.setFont('helvetica','bold');
    if (y > 280) {
      doc.addPage();
      y=20;
    }
    doc.text(title,15,y);
    y +=5;
    doc.setFont('helvetica','normal');
    const lines=doc.splitTextToSize(text,180);
    for (const line of lines) {
      if (y > 280) {
        doc.addPage();
        y=20;
      }
      doc.text(line,15,y);
      y +=4;
    }
    return y + 6;
  };

  if (
    invoiceDefaults?.payIdEnabled === true &&
    safeText(invoiceDefaults.payId).trim() &&
    safeText(invoiceDefaults.payIdName).trim()
  ) {
    const payIdValue=safeText(invoiceDefaults.payId).trim();
    const payIdName=safeText(invoiceDefaults.payIdName).trim();
    const reference=safeText(invoice.invoiceNumber).trim();
    const detailsLines=[
      `PayID: ${payIdValue}`,
      `PayID name: ${payIdName}`,
      ...(reference ? [`Reference: ${reference}`] : [])
    ];

    doc.setFont('helvetica','normal');
    doc.setFontSize(8.5);
    const wrappedDetails=detailsLines.flatMap(line=> doc.splitTextToSize(line,180));
    const reservedHeight=50 + Math.max(0,wrappedDetails.length - detailsLines.length) * 4;
    if (currentY + reservedHeight > invoicePageHeight - 12) {
      doc.addPage();
      currentY=20;
    }

    const payIdLogoDataUrl=await resolvePayIdBrandmarkPngDataUrl();
    if (payIdLogoDataUrl) {
      doc.addImage(payIdLogoDataUrl,'PNG',15,currentY,42,14);
    }

    const textX=payIdLogoDataUrl ? 62 : 15;
    doc.setTextColor(0,0,0);
    doc.setFont('helvetica','bold');
    doc.setFontSize(10);
    doc.text('PayID® payments',textX,currentY + 4);
    doc.setFont('helvetica','normal');
    doc.setFontSize(7.8);
    doc.text('Pay securely using your banking app. Confirm the recipient name before paying.',textX,currentY + 9);

    let detailY=currentY + 18;
    doc.setFontSize(8.5);
    for (const line of detailsLines) {
      const wrappedLines=doc.splitTextToSize(line,180);
      for (const wrappedLine of wrappedLines) {
        doc.text(wrappedLine,15,detailY);
        detailY +=4;
      }
    }

    currentY=Math.max(detailY + 1,currentY + 31);
    currentY=renderLongText(
      'Trademark notice:',
      'PayID is a registered trade mark of NPP Australia Ltd ABN 68 601 428 737.',
      currentY
    );
  }

  const normaliseTermsTextForPdf=(value: string): string=> {
    let text=safeText(value)
      .replace(/\r\n/g,'\n')
      .replace(/\r/g,'\n');
    if (/<[a-z][\s\S]*>/i.test(text)) {
      const container=document.createElement('div');
      container.innerHTML=text;
      const toPlainText=(node: Node): string=> {
        if (node.nodeType===Node.TEXT_NODE) {
          return node.textContent || '';
        }
        if (node.nodeType !==Node.ELEMENT_NODE) {
          return '';
        }
        const element=node as HTMLElement;
        const tag=element.tagName.toUpperCase();
        const children=Array.from(element.childNodes)
          .map(toPlainText)
          .join('');
        if (tag==='BR') {
          return '\n';
        }
        if (tag==='LI') {
          const parentTag=element.parentElement?.tagName.toUpperCase();
          if (parentTag==='OL') {
            const siblings=Array.from(element.parentElement?.children || []);
            return `${siblings.indexOf(element) + 1}. ${children}\n`;
          }
          return `- ${children}\n`;
        }
        if (tag==='P' || tag==='DIV') {
          return `${children}\n`;
        }
        return children;
      };
      text=Array.from(container.childNodes)
        .map(toPlainText)
        .join('');
    }
    return text
      .replace(/\u00a0/g,' ')
      .replace(/[\u2018\u2019]/g,"'")
      .replace(/[\u201c\u201d]/g,'"')
      .replace(/[\u2013\u2014]/g,'-')
      .replace(/\u2022/g,'-')
      .replace(/[ \t]+\n/g,'\n')
      .replace(/\n{3,}/g,'\n\n')
      .trim();
  };

  const renderBrandedInvoiceTermsAppendix=(text: string)=> {
    const marginX=15;
    const rightEdge=195;
    const dividerY=42;
    const bodyStartY=50;
    const lineHeight=4;
    const continuationIndent=5;
    const maxY=278;
    const footerStartY=284;

    const renderAppendixFooter=()=> {
      doc.setFont('helvetica','normal');
      doc.setFontSize(7.5);
      doc.setTextColor(100,100,100);
      doc.text('Terms Appendix',105,footerStartY,{align: 'center'});
      doc.text(`${safeText(businessSettings.businessName || 'BPA Solutions')} | Invoice ${safeText(invoice.invoiceNumber)} | Terms Appendix`,105,footerStartY + 5,{align: 'center'});
    };

    const renderAppendixHeader=()=> {
      let appendixLogoY=12;
      if (logoDataUrl) {
        try {
          const logoFormat=/^data:image\/jpe?g/i.test(logoDataUrl) ? 'JPEG' : 'PNG';
          const imageProperties=doc.getImageProperties(logoDataUrl);
          const maxLogoWidth=34;
          const maxLogoHeight=14;
          const originalWidth=Number(imageProperties.width) || maxLogoWidth;
          const originalHeight=Number(imageProperties.height) || maxLogoHeight;
          const ratio=Math.min(maxLogoWidth / originalWidth,maxLogoHeight / originalHeight);
          const logoWidth=originalWidth * ratio;
          const logoHeight=originalHeight * ratio;
          doc.addImage(logoDataUrl,logoFormat,marginX,12,logoWidth,logoHeight);
          appendixLogoY=12 + logoHeight + 5;
        } catch (error) {
          console.warn('Appendix logo embedding failed.',error);
        }
      }
      doc.setTextColor(...accentRgb);
      doc.setFont('helvetica','bold');
      doc.setFontSize(16);
      doc.text('INVOICE TERMS',rightEdge,16,{align: 'right'});
      doc.setFontSize(12);
      doc.text('AND CONDITIONS',rightEdge,24,{align: 'right'});
      doc.setFont('helvetica','normal');
      doc.setFontSize(9);
      doc.text(`Invoice: ${safeText(invoice.invoiceNumber)}`,rightEdge,33,{align: 'right'});
      doc.setTextColor(0,0,0);
      doc.setFont('helvetica','bold');
      doc.setFontSize(9);
      doc.text(safeText(businessSettings.businessName),marginX,Math.max(appendixLogoY,31));
      doc.setFont('helvetica','normal');
      if (businessSettings.email) {
        doc.text(safeText(businessSettings.email),marginX,Math.max(appendixLogoY,31) + 5);
      }
      doc.setDrawColor(...accentRgb);
      doc.line(marginX,dividerY,rightEdge,dividerY);
    };

    doc.addPage();
    renderAppendixHeader();
    let currentY=bodyStartY;

    const startContinuationPage=()=> {
      renderAppendixFooter();
      doc.addPage();
      renderAppendixHeader();
      currentY=bodyStartY;
    };

    const ensureAppendixSpace=(requiredHeight=lineHeight)=> {
      if (currentY + requiredHeight > maxY) {
        startContinuationPage();
      }
    };

    const sourceLines=normaliseTermsTextForPdf(text).split('\n');
    for (const rawLine of sourceLines) {
      const line=rawLine.trimEnd();
      if (!line.trim()) {
        currentY +=3;
        continue;
      }
      const trimmed=line.trim();
      const isSectionHeading=/^\d+\.\s+[A-Z][A-Z0-9 &,/()'-]+$/.test(trimmed) || (/^[A-Z][A-Z0-9 &,/()'-]+$/.test(trimmed) && trimmed.length < 80);
      if (isSectionHeading) {
        ensureAppendixSpace(10);
        currentY +=1;
        doc.setFont('helvetica','bold');
        doc.setFontSize(10);
        doc.setTextColor(...accentRgb);
        doc.text(trimmed,marginX,currentY);
        currentY +=6;
        continue;
      }
      const bulletMatch=line.match(/^(\s*[-*]\s+)(.*)$/);
      const numberMatch=line.match(/^(\s*\d+(?:\.\d+)*(?:[.)])?\s+)(.*)$/);
      const prefix=bulletMatch ? bulletMatch[1] : numberMatch ? numberMatch[1] : '';
      const content=bulletMatch ? bulletMatch[2] : numberMatch ? numberMatch[2] : line;
      doc.setFontSize(9);
      if (prefix) {
        const prefixWidth=doc.getTextWidth(prefix);
        const contentX=marginX + prefixWidth;
        const wrapWidth=180 - prefixWidth - continuationIndent;
        ensureAppendixSpace();
        doc.setFont('helvetica','bold');
        doc.setTextColor(...accentRgb);
        doc.text(prefix,marginX,currentY);
        const wrappedLines=doc.splitTextToSize(content,wrapWidth);
        wrappedLines.forEach((wrappedLine: string,index: number)=> {
          ensureAppendixSpace();
          doc.setFont('helvetica','normal');
          doc.setTextColor(0,0,0);
          doc.text(wrappedLine,index===0 ? contentX : contentX + continuationIndent,currentY);
          currentY +=lineHeight;
        });
      } else {
        const wrappedLines=doc.splitTextToSize(line,180);
        wrappedLines.forEach((wrappedLine: string)=> {
          ensureAppendixSpace();
          doc.setFont('helvetica','normal');
          doc.setTextColor(0,0,0);
          doc.text(wrappedLine,marginX,currentY);
          currentY +=lineHeight;
        });
      }
      currentY +=1;
    }
    renderAppendixFooter();
  };

  if (invoice.notes) {
    currentY=renderLongText('Notes:',safeText(invoice.notes),currentY);
  }

  const resolvedTerms=safeText(invoice.termsSnapshot)
    .replace(/\r\n/g,'\n')
    .trim() || safeText(invoiceDefaults?.paymentTerms)
    .replace(/\r\n/g,'\n')
    .trim();

  if (resolvedTerms) {
    renderBrandedInvoiceTermsAppendix(resolvedTerms);
  }

  if (shouldShowPaidStamp(invoice)) {
    const pageCount=doc.getNumberOfPages();
    for (let page=1;page <=pageCount;page++) {
      doc.setPage(page);
      drawPaidStamp(doc);
    }
    doc.setPage(pageCount);
  }

  const pdfBlob=doc.output('blob');
  const pdfUrl=URL.createObjectURL(pdfBlob);
  const filename=`invoice-${invoice.invoiceNumber}-${Date.now()}.pdf`;
  return {pdfBlob,pdfUrl,filename};
};

export const generatePaymentDocumentPdf=async (
  invoice: Invoice,
  client: Client,
  payment: {id: string;type: string;date: string;amount: number;method: string;reference?: string;notes?: string;},
  previousBalance: number,
  documentType: 'payment_receipt' | 'remittance_advice',
  businessSettings: BusinessSettings,
  invoiceDefaults?: InvoiceDefaults
)=> {
  const doc=new jsPDF() as any;
  const logoDataUrl=await resolveLogoDataUrl(businessSettings);
  const accentRgb=hexToRgb(invoiceDefaults?.pdfAccentColor || DEFAULT_PDF_ACCENT);

  if (logoDataUrl) {
    const logoFormat=/^data:image\/jpe?g/i.test(logoDataUrl) ? 'JPEG' : 'PNG';
    const imageProperties=doc.getImageProperties(logoDataUrl);
    const maxLogoWidth=46;
    const maxLogoHeight=18;
    const originalWidth=Number(imageProperties.width) || maxLogoWidth;
    const originalHeight=Number(imageProperties.height) || maxLogoHeight;
    const ratio=Math.min(maxLogoWidth / originalWidth,maxLogoHeight / originalHeight);
    const logoWidth=originalWidth * ratio;
    const logoHeight=originalHeight * ratio;
    doc.addImage(logoDataUrl,logoFormat,15,12,logoWidth,logoHeight);
  }

  const title=documentType==='remittance_advice' ? 'REMITTANCE ADVICE' : 'PAYMENT RECEIPT';
  const documentPrefix=documentType==='remittance_advice' ? 'REM' : 'REC';
  const documentNumber=`${documentPrefix}-${String(payment.id).slice(-8).toUpperCase()}`;

  doc.setTextColor(...accentRgb);
  doc.setFont('helvetica','bold');
  doc.setFontSize(18);
  doc.text(title,195,20,{align: 'right'});
  doc.setTextColor(30,41,59);
  doc.setFontSize(10);
  doc.setFont('helvetica','normal');
  doc.text(safeText(businessSettings.businessName || businessSettings.tradingName || 'BPA Solutions'),15,42);
  doc.text(`ABN: ${safeText(businessSettings.abn)}`,15,48);
  doc.text(`Document Number: ${documentNumber}`,15,62);
  doc.text(`Invoice Number: ${safeText(invoice.invoiceNumber)}`,15,68);
  doc.text(`Payment Date: ${formatDate(payment.date)}`,15,74);
  doc.text(`Client: ${safeText(client.name || client.companyName)}`,15,86);
  if (client.companyName) {
    doc.text(`Company: ${safeText(client.companyName)}`,15,92);
  }

  const startY=client.companyName ? 108 : 102;
  (doc as any).autoTable({
    startY,
    head: [['Description','Amount']],
    body: [
      ['Previous invoice balance',formatCurrency(previousBalance,invoice.currency)],
      ['Payment received',formatCurrency(Number(payment.amount || 0),invoice.currency)],
      ['Remaining balance',formatCurrency(Number(invoice.balanceDue || 0),invoice.currency)]
    ],
    theme: 'grid',
    headStyles: {fillColor: accentRgb}
  });

  const finalY=(doc as any).lastAutoTable.finalY + 12;
  doc.setFontSize(10);
  doc.text(`Payment method: ${safeText(payment.method)}`,15,finalY);
  doc.text(`Reference: ${safeText(payment.reference || '-')}`,15,finalY + 6);
  if (payment.notes) {
    doc.text(`Notes: ${safeText(payment.notes)}`,15,finalY + 12,{maxWidth: 180});
  }

  doc.setFontSize(8);
  doc.setTextColor(100,116,139);
  doc.text(
    documentType==='remittance_advice' ? 'This document records the remittance information associated with the referenced invoice.' : 'Thank you. This receipt records the payment received and the remaining invoice balance.',
    15,282,
    {maxWidth: 180}
  );

  const pdfBlob=doc.output('blob');
  const pdfUrl=URL.createObjectURL(pdfBlob);
  const filename=`${documentType}-${invoice.invoiceNumber}-${Date.now()}.pdf`;
  return {pdfBlob,pdfUrl,filename,documentNumber};
};

export const downloadPdf=(pdfUrl: string,filename: string)=> {
  const a=document.createElement('a');
  a.href=pdfUrl;
  a.download=filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};